import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, Check, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';

const ComponentImport = ({ facilityId, onImportComplete }) => {
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(sheet);

        // Preview first 10 rows
        setPreview({
          filename: file.name,
          data: data.slice(0, 10),
          total: data.length,
          file
        });
        setResult(null);
      } catch (error) {
        console.error('Error reading file:', error);
        setResult({ success: false, message: 'Datei konnte nicht gelesen werden' });
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!preview?.file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', preview.file);

      const res = await fetch(`/api/facilities/${facilityId}/components/import`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        setResult({ success: true, message: `${data.count} Komponenten importiert` });
        setPreview(null);
        if (onImportComplete) onImportComplete();
      } else {
        setResult({ success: false, message: data.message || 'Import fehlgeschlagen' });
      }
    } catch (error) {
      setResult({ success: false, message: 'Verbindungsfehler' });
    }
    setImporting(false);
  };

  const handleDownloadSample = () => {
    window.location.href = '/api/components/sample-csv';
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-md">
      <h3 className="text-lg font-semibold text-[#433225] mb-4 flex items-center gap-2">
        <FileSpreadsheet className="w-5 h-5" />
        Komponenten importieren
      </h3>

      {/* Info */}
      <div className="mb-4 p-3 bg-[#f7e6d8] rounded-lg text-sm text-[#433225]">
        <p className="font-medium mb-1">Erwartete Spalten:</p>
        <ul className="list-disc list-inside text-[#433225]/70">
          <li>Nummer (Pflicht)</li>
          <li>Name (Pflicht) - für Alarm-Zuordnung</li>
          <li>Bereich (Pflicht) - für Alarm-Zuordnung</li>
          <li>Typ (Optional) - Rauchmelder, Handtaster, Sirene, etc.</li>
        </ul>
      </div>

      {/* File input */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#e8d5c7] rounded-lg hover:border-[#e89f71] hover:bg-[#f7e6d8] transition-colors"
        >
          <Upload className="w-5 h-5 text-[#433225]" />
          <span className="text-[#433225]">CSV oder Excel hochladen</span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
        />

        <button
          onClick={handleDownloadSample}
          className="flex items-center gap-2 px-4 py-3 bg-[#e8d5c7] rounded-lg hover:bg-[#dcc5b7] text-[#433225]"
          title="Beispiel-Vorlage herunterladen"
        >
          <Download className="w-5 h-5" />
        </button>
      </div>

      {/* Preview */}
      {preview && (
        <div className="mb-4">
          <p className="text-sm text-[#433225] mb-2">
            <strong>{preview.filename}</strong> - {preview.total} Einträge
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#f7e6d8]">
                  {Object.keys(preview.data[0] || {}).map(key => (
                    <th key={key} className="px-3 py-2 text-left text-[#433225] font-medium border border-[#e8d5c7]">
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.data.map((row, i) => (
                  <tr key={i} className="hover:bg-[#f7e6d8]/50">
                    {Object.values(row).map((val, j) => (
                      <td key={j} className="px-3 py-2 border border-[#e8d5c7] text-[#433225]">
                        {String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.total > 10 && (
            <p className="text-sm text-[#433225]/60 mt-1">
              ...und {preview.total - 10} weitere
            </p>
          )}

          <button
            onClick={handleImport}
            disabled={importing}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#e89f71] text-white rounded-lg hover:bg-[#d88f61] disabled:opacity-50"
          >
            {importing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Importieren...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {preview.total} Komponenten importieren
              </>
            )}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`flex items-center gap-2 p-3 rounded-lg ${
          result.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {result.success ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {result.message}
        </div>
      )}
    </div>
  );
};

export default ComponentImport;
